import { literals } from "@/constants/literals";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import { formatBooleanText, formatOptionalText } from "@/lib/texts.utils";
import type { RequestPhotoInfo } from "@/types/types.d";
import { RequestModalDetail } from "./RequestModalDetail";
import { NACELLE_CONFIGURATION } from "@/constants/constants";
import DescriptionEditionContent from "./DescriptionEditionContent";
import InputText from "@/components/input-text/InputText";
import EditionModalRadioGroup from "./EditionModalRadioGroup";
import InputNumber from "@/components/input-number/InputNumber";

export type RequestEditionInputsProps = {
  photoURL: string;
  image: RequestPhotoInfo;
  editedData: any;
  changeEditedData: (
    value: string | boolean | undefined | null,
    field: keyof RequestPhotoInfo,
  ) => void;
};

const RequestEditionInputs = ({
  photoURL,
  image,
  editedData,
  changeEditedData,
}: RequestEditionInputsProps) => {
  const isNacelleFindings =
    image?.step === NACELLE_CONFIGURATION.FINDINGS &&
    image.area === NACELLE_CONFIGURATION.GARAGE_ID;

  return (
    <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-1">
      <div className="w-full flex flex-row justify-start items-center gap-x-4">
        <div className="w-1/2">
          {photoURL && (
            <img
              src={photoURL}
              alt={image.fileName}
              className="w-full rounded"
            />
          )}
        </div>
        <RequestModalDetail
          label={literals.requestedBy}
          value={formatOptionalText(image.requestedBy)}
        />
      </div>
      <div className="border border-ib-pt-color-red-500 rounded-md pt-2 px-2 my-2">
        <RequestModalDetail
          label={literals.request}
          value={image.editionRequest as string}
        />
      </div>
      <div className="pb-3 space-y-3 mt-3">
        <div className="grid grid-cols-3 gap-x-2">
          <RequestModalDetail
            label={literals.date}
            value={formatDateDDMmmYYYY(image.createdAt)}
          />
          <RequestModalDetail label={literals.workstation} value={image.area} />
          <RequestModalDetail label={literals.step} value={image.step} />
        </div>
        <DescriptionEditionContent
          step={image.step}
          isNacelleFindings={isNacelleFindings}
          changeDescription={(data) =>
            changeEditedData(data, "imageGroupDescription")
          }
          description={image.imageGroupDescription}
        />
        <div className="grid grid-cols-2 gap-x-4">
          <EditionModalRadioGroup
            label={literals.visibleClient}
            selected={editedData.imageGroupVisible}
            onChange={(data) => changeEditedData(data, "imageGroupVisible")}
          />
          <div className="mb-4">
            <InputText
              label={literals.customerOrderNoServ}
              placeholder={literals.customerOrderNoServ}
              value={editedData.customerOrderNoserv}
              size="medium"
              onChange={(data) => changeEditedData(data, "customerOrderNoserv")}
            />
          </div>
          <InputNumber
            label={literals.workOrder}
            placeholder={literals.workOrder}
            value={editedData.sapWorkOrder}
            size="medium"
            onChange={(data) => changeEditedData(data, "sapWorkOrder")}
            helpText={literals.orderNumberFormat}
            error={editedData.sapWorkOrder?.length > 12}
            required={
              image?.sapWorkOrder?.length && image?.sapWorkOrder?.length > 0
                ? true
                : false
            }
          />
          <div className="mb-4">
            <InputText
              label={literals.returnToOrigin}
              placeholder={literals.returnToOrigin}
              value={formatBooleanText(image.originReturn)}
              size="medium"
              onChange={() => {}}
              disabled
            />
          </div>
          {image.originReturn === true ? (
            <div className="mb-4">
              <InputNumber
                label={literals.document9R1}
                placeholder={literals.document9R1}
                value={editedData.document9r1}
                size="medium"
                onChange={() => {}}
                disabled={image.originReturn === true}
              />
            </div>
          ) : (
            <InputNumber
              label={literals.document9R1}
              placeholder={literals.document9R1}
              value={editedData.document9r1}
              size="medium"
              onChange={(data) => changeEditedData(data, "document9r1")}
              helpText={literals.r9Format}
              error={editedData.document9r1?.length > 10}
              required={
                image?.document9r1?.length && image?.document9r1?.length > 0
                  ? true
                  : false
              }
            />
          )}
          <div className="mb-4">
            <InputText
              label={literals.partIn}
              placeholder={literals.partIn}
              value={editedData.orderIn}
              size="medium"
              onChange={(data) => changeEditedData(data, "orderIn")}
            />
          </div>
          <div className="mb-4">
            <InputText
              label={literals.serialIn}
              placeholder={literals.serialIn}
              value={editedData.serialIn}
              size="medium"
              onChange={(data) => changeEditedData(data, "serialIn")}
            />
          </div>
          <div className="mb-4">
            <InputText
              label={literals.partOut}
              placeholder={literals.partOut}
              value={editedData.orderOut}
              size="medium"
              onChange={(data) => changeEditedData(data, "orderOut")}
            />
          </div>
          <div className="mb-4">
            <InputText
              label={literals.serialOut}
              placeholder={literals.serialOut}
              value={editedData.serialOut}
              size="medium"
              onChange={(data) => changeEditedData(data, "serialOut")}
            />
          </div>
          <InputNumber
            label={literals.client}
            placeholder={literals.client}
            value={editedData.customer}
            size="medium"
            onChange={(data) => changeEditedData(data, "customer")}
            helpText={literals.customerFormat}
            error={editedData.customer?.length > 6}
            required={
              image?.customer?.length && image?.customer?.length > 0
                ? true
                : false
            }
          />
          <div className="mb-4">
            <InputText
              label={literals.packagingReference}
              placeholder={literals.packagingReference}
              value={formatOptionalText(editedData.packagingReference)}
              size="medium"
              onChange={() => {}}
              disabled
            />
          </div>
          <EditionModalRadioGroup
            label={literals.ownPackaging}
            selected={editedData.ownPackaging}
            onChange={(data) => changeEditedData(data, "ownPackaging")}
          />
          <EditionModalRadioGroup
            label={literals.correctPackaging}
            selected={editedData.correctPackaging}
            onChange={(data) => changeEditedData(data, "correctPackaging")}
          />
          <EditionModalRadioGroup
            label={literals.damagedPackaging}
            selected={editedData.damagedPackaging}
            onChange={(data) => changeEditedData(data, "damagedPackaging")}
          />
          <EditionModalRadioGroup
            label={literals.ownPackagingOut}
            selected={editedData.ownPackagingOut}
            onChange={(data) => changeEditedData(data, "ownPackagingOut")}
          />
          <EditionModalRadioGroup
            label={literals.iberiaPackagingOut}
            selected={editedData.iberiaPackagingOut}
            onChange={(data) => changeEditedData(data, "iberiaPackagingOut")}
          />
        </div>
      </div>
    </div>
  );
};
export default RequestEditionInputs;
